import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ADMIN_REPORT_CATALOG,
  STATUS_LABEL,
  STATUS_TONE,
  type AdminReportDefinition,
  type AdminSurfaceStatus,
} from '../../platform/adminPlatformModel';
import { getGovernanceReport, type GovernanceReport } from '../_shared/adminTelemetryApi';
import { actionLabel } from '../audit/auditModel';
import { roleLabel } from '../users/userModel';
import './ReportsPage.css';

function StatusPill({ status }: { status: AdminSurfaceStatus }) {
  return <span className={`pill pill-dot ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

function EntryLink({ route }: { route?: string }) {
  if (!route) return <span className="text-tertiary">尚無入口</span>;
  return (
    <Link className="btn-ghost btn-sm" to={route}>
      開啟
    </Link>
  );
}

// 開發者除錯用端點徽章:平常只留一個資訊符號,hover/focus 才看完整端點字串。
function DevHint({ endpoint }: { endpoint: string }) {
  return (
    <span className="rpt-dev-hint" title={endpoint} aria-label={`資料端點 ${endpoint}`}>
      ⓘ
    </span>
  );
}

// KPI 指標卡:label(可帶警示點)+ value(可警示色)+ foot
function KpiCard({
  label,
  value,
  foot,
  secondary,
  warn,
}: {
  label: string;
  value: ReactNode;
  foot: string;
  secondary?: boolean;
  warn?: boolean;
}) {
  return (
    <div className={`kpi-card${secondary ? ' kpi-card--secondary' : ''}`}>
      <span className="kpi-label">
        {warn && <span className="rpt-kpi-dot" aria-hidden="true" />}
        {label}
      </span>
      <span className={`kpi-value${warn ? ' rpt-kpi-value--warn' : ''}`}>{value}</span>
      <span className="kpi-foot">{foot}</span>
    </div>
  );
}

// 報表目錄單列:table-to-card hybrid — 主列強調報表名/狀態/入口,敘述欄位降階為次列小字。
function CatalogCard({ report }: { report: AdminReportDefinition }) {
  return (
    <div className="card rpt-catalog-item" data-testid={`report-${report.id}`}>
      <div className="rpt-catalog-main">
        <div className="rpt-catalog-heading">
          <div className="rpt-catalog-name">
            <span className="rpt-catalog-title">{report.title}</span>
            <span className="text-tertiary">{report.domain}</span>
          </div>
        </div>
        <div className="rpt-catalog-actions">
          <StatusPill status={report.status} />
          <EntryLink route={report.route} />
        </div>
      </div>
      <div className="rpt-catalog-detail">
        <div className="rpt-detail-pair">
          <span className="rpt-detail-label">回答的問題</span>
          <span className="rpt-detail-value">{report.question}</span>
        </div>
        <div className="rpt-detail-pair">
          <span className="rpt-detail-label">資料源</span>
          <span className="rpt-detail-value mono">{report.source}</span>
        </div>
        <div className="rpt-detail-pair">
          <span className="rpt-detail-label">新鮮度 / 缺口</span>
          <span className="rpt-detail-value">{report.freshness}</span>
        </div>
        <div className="rpt-detail-pair">
          <span className="rpt-detail-label">故障段</span>
          <span className="rpt-detail-value mono">{report.failureSegment}</span>
        </div>
        <div className="rpt-detail-pair">
          <span className="rpt-detail-label">下鑽</span>
          <span className="rpt-detail-value">{report.drilldown}</span>
        </div>
      </div>
    </div>
  );
}

// R17 治理報表(restful!151):端點未部署 → 'unavailable',保留 catalog 殼。
type GovState = GovernanceReport | null | 'unavailable';

function useGovernance(days: number): GovState {
  const [gov, setGov] = useState<GovState>(null);
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    getGovernanceReport(days, controller.signal)
      .then((r) => alive && setGov(r.data ?? 'unavailable'))
      .catch(() => alive && setGov('unavailable'));
    return () => {
      alive = false;
      controller.abort();
    };
  }, [days]);
  return gov;
}

// 處置動作分布卡:横向 mini bar(依最大值等比)。
function ActionBreakdownCard({ byAction }: { byAction: Record<string, number> }) {
  const rows = Object.entries(byAction).sort((a, b) => b[1] - a[1]);
  const max = rows.reduce((m, [, count]) => Math.max(m, count), 0) || 1;
  return (
    <div className="card">
      <div className="rpt-gov-card-head">
        <h3 className="rpt-gov-card-title">處置動作分布</h3>
        <p className="rpt-gov-card-caption">近 7 日各類管理處置動作的發生次數,長條依窗內最大值等比。</p>
      </div>
      {rows.length === 0 ? (
        <div className="empty-state empty-state--inline">
          <div className="empty-state-icon" aria-hidden="true">🗂️</div>
          <div className="empty-state-title">窗內沒有處置紀錄</div>
          <div className="empty-state-detail">近 7 日尚無任何管理處置動作。</div>
        </div>
      ) : (
        <div className="rpt-bar-list">
          {rows.map(([action, count]) => (
            <Fragment key={action}>
              <span className="rpt-bar-action" title={action}>{actionLabel(action)}</span>
              <div className="rpt-bar-track">
                <span className="rpt-bar-fill" style={{ width: `${Math.round((count / max) * 100)}%` }} />
                <span className="rpt-bar-count">{count}</span>
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// 維運者活動卡:各維運者的角色與動作數。
function ActorActivityCard({ actors }: { actors: GovernanceReport['actors'] }) {
  const list = actors ?? [];
  return (
    <div className="card">
      <div className="rpt-gov-card-head">
        <h3 className="rpt-gov-card-title">維運者活動</h3>
        <p className="rpt-gov-card-caption">近 7 日各維運者的角色與處置動作數(與左卡各自獨立,非逐列對應)。</p>
      </div>
      {list.length === 0 ? (
        <div className="empty-state empty-state--inline">
          <div className="empty-state-icon" aria-hidden="true">👤</div>
          <div className="empty-state-title">名冊內沒有維運者</div>
          <div className="empty-state-detail">近 7 日尚無維運者的處置活動。</div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>維運者</th>
                <th>角色</th>
                <th>動作數</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.actor_user_id}>
                  <td>{a.email || a.actor_user_id}</td>
                  <td>{a.role ? <span className="pill pill-neutral" title={a.role}>{roleLabel(a.role)}</span> : '—'}</td>
                  <td>{a.actions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// loading 佔位:兩張骨架卡,避免版面塌陷。
function GovernanceSkeletonCard() {
  return (
    <div className="card rpt-gov-skeleton">
      <div className="rpt-gov-card-head">
        <div className="skeleton-bar" style={{ width: '40%' }} />
        <div className="skeleton-bar" style={{ width: '70%' }} />
      </div>
      <div className="skeleton-bar" />
      <div className="skeleton-bar" />
      <div className="skeleton-bar" />
      <div className="skeleton-bar" />
    </div>
  );
}

export default function ReportsPage() {
  const governance = useGovernance(7);

  const total = ADMIN_REPORT_CATALOG.length;
  const liveCount = ADMIN_REPORT_CATALOG.filter((report) => report.status === 'live').length;
  const gapCount = ADMIN_REPORT_CATALOG.filter(
    (report) => report.status === 'planned' || report.status === 'blocked',
  ).length;

  return (
    <div className="rpt-page" data-testid="reports-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">分析報表</h1>
          <p className="page-subtitle">平台業務、機隊、韌體、開發者、安全與用量報表的資料源與可用狀態。</p>
        </div>
      </div>

      {/* 報表目錄自身的覆蓋統計(平台即時數據已是 dashboard 的家,不在此重複) */}
      <div className="rpt-kpi-groups" data-testid="reports-kpi-strip" aria-label="報表指標">
        <div className="rpt-kpi-group rpt-kpi-group--secondary">
          <span className="rpt-kpi-group-label">報表覆蓋(目錄統計)</span>
          <p className="rpt-kpi-group-note">來自本頁報表目錄,非平台即時數據。</p>
          <div className="kpi-strip">
            <KpiCard label="報表總數" value={total} foot="已編目管理報表" secondary />
            <KpiCard label="可操作" value={liveCount} foot="現有端點完整支撐" secondary />
            <KpiCard
              label="缺口"
              value={gapCount}
              foot="待報表聚合端點 / 決策"
              secondary
              warn={gapCount > 0}
            />
          </div>
        </div>
      </div>

      <section data-testid="reports-governance">
        <div className="section-head">
          <h2 className="section-title">治理報表(近 7 日)</h2>
          <span className="section-meta">
            R17 第一批
            <DevHint endpoint="GET /v3/admin/reports/governance" />
          </span>
        </div>

        {governance === null && (
          <div className="rpt-gov-stack">
            <GovernanceSkeletonCard />
            <GovernanceSkeletonCard />
          </div>
        )}

        {governance === 'unavailable' && (
          <div className="card">
            <div className="rpt-unavailable">
              <div className="rpt-unavailable-icon" aria-hidden="true">🚧</div>
              <div className="rpt-unavailable-body">
                <span className="rpt-unavailable-title">治理報表端點尚未部署</span>
                <span className="rpt-unavailable-detail">
                  R17 報表聚合端點(restful!151)上線後,此區將自動接上近 7 日的處置動作分布與維運者活動。
                </span>
                <span className="rpt-unavailable-hint">GET /v3/admin/reports/governance</span>
              </div>
            </div>
          </div>
        )}

        {governance !== null && governance !== 'unavailable' && (
          <div className="rpt-gov-stack">
            <ActionBreakdownCard byAction={governance.by_action ?? {}} />
            <ActorActivityCard actors={governance.actors} />
          </div>
        )}
      </section>

      <section>
        <div className="section-head">
          <h2 className="section-title">報表目錄</h2>
          <span className="section-meta">可操作 / 已接端點 / 待端點 資料源盤點</span>
        </div>
        <div className="rpt-catalog-list" data-testid="reports-catalog">
          {ADMIN_REPORT_CATALOG.map((report) => (
            <CatalogCard key={report.id} report={report} />
          ))}
        </div>
      </section>
    </div>
  );
}
